"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  CopyIcon,
  MoreHorizontalIcon,
  PlusIcon,
  SettingsIcon,
} from "lucide-react";
import Link from "next/link";
import React from "react";
import SxIcon from "@/assets/icons/sx.svg";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ADS_POLICIES } from "@/lib/authorization/policies/ads-analytics";
import { IW_POLICIES } from "@/lib/authorization/policies/index-worker";
import { SA_POLICIES } from "@/lib/authorization/policies/search-analytics";
import { UA_POLICIES } from "@/lib/authorization/policies/user-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import { cn } from "@/lib/utils";
import { DuplicateProjectDialog } from "../../dialog/duplicate-project-dialog";

interface ProjectMoreMenuProps {
  projectId: string;
  projectType: "ua" | "sa" | "iw" | "ads";
  workspaceId: string;
  projectName: string;
  lang?: string;
  onCreateViewFromPreset?: () => void;
  additionalMenuItems?: React.ReactNode;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const POLICY_MAP = {
  iw: {
    project: IW_POLICIES["iw-project:edit"],
    view: IW_POLICIES["iw-job:edit"],
  },
  sa: {
    project: SA_POLICIES["sa-project:edit"],
    view: SA_POLICIES["sa-view:edit"],
  },
  ua: {
    project: UA_POLICIES["ua-project:edit"],
    view: UA_POLICIES["ua-view:edit"],
  },
  ads: {
    project: ADS_POLICIES["ads-project:edit"],
    view: ADS_POLICIES["ads-view:edit"],
  },
} as const;

export function ProjectMoreMenu({
  projectId,
  projectType,
  workspaceId,
  projectName,
  lang,
  onCreateViewFromPreset,
  additionalMenuItems,
  className,
  open,
  onOpenChange,
}: ProjectMoreMenuProps) {
  const { i18n } = useLingui();
  const [duplicateDialogOpen, setDuplicateDialogOpen] = React.useState(false);
  const checkPolicy = useCheckPolicy();

  const { project: projectPolicy, view: viewPolicy } = POLICY_MAP[projectType];
  const canDuplicate = projectType !== "ads" && checkPolicy(projectPolicy).ok;
  const canEditViews = checkPolicy(viewPolicy).ok;
  const canOpenSettings = checkPolicy(projectPolicy).ok;
  const canCreateView = projectType === "ua" && canEditViews;
  const hasAdditionalItems = Boolean(additionalMenuItems);

  const settingsHref =
    projectType === "ads" && lang
      ? `/${encodeURIComponent(lang)}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(projectId)}/settings`
      : `/${encodeURIComponent(workspaceId)}/${projectType}/${encodeURIComponent(projectId)}/settings`;

  if (
    !canDuplicate &&
    !canOpenSettings &&
    !canCreateView &&
    !hasAdditionalItems
  ) {
    return null;
  }

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <Tooltip disableHoverableContent>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className={cn("h-6 w-6 pointer-events-auto", className)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
            >
              <MoreHorizontalIcon className="h-3.5 w-3.5" />
              <span className="sr-only">
                <Trans>More options</Trans>
              </span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {projectType === "ua"
            ? i18n._(msg`Create view from preset, settings, etc...`)
            : i18n._(msg`Project duplication, settings, etc...`)}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        side="bottom"
        sideOffset={4}
        alignOffset={-80}
        className="w-auto min-w-[12rem]"
        onCloseAutoFocus={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
      >
        {additionalMenuItems ? (
          <>
            {additionalMenuItems}
            {(canCreateView && onCreateViewFromPreset) ||
            (projectType === "ua" && canEditViews) ||
            canDuplicate ||
            canOpenSettings ? (
              <DropdownMenuSeparator />
            ) : null}
          </>
        ) : null}

        {canCreateView && onCreateViewFromPreset ? (
          <>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCreateViewFromPreset();
              }}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              <span className="flex items-center gap-2">
                <Trans>Create View from Preset</Trans>
                <span className="inline-flex items-center h-4 px-1.5 text-[11px] font-medium bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-full">
                  Beta
                </span>
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {projectType === "ua" && canEditViews ? (
          <DropdownMenuItem
            onSelect={(e) => {
              e.stopPropagation();
            }}
            asChild
          >
            <Link
              href={`/${encodeURIComponent(workspaceId)}/${projectType}/${encodeURIComponent(
                projectId,
              )}/session_explorer`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SxIcon className="mr-2 h-4 w-4" />
              <Trans>Session Explorer</Trans>
            </Link>
          </DropdownMenuItem>
        ) : null}

        {canDuplicate ? (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDuplicateDialogOpen(true);
            }}
          >
            <CopyIcon className="mr-2 h-4 w-4" />
            <Trans>Duplicate Project</Trans>
          </DropdownMenuItem>
        ) : null}

        {canOpenSettings ? (
          <DropdownMenuItem
            onSelect={(e) => {
              e.stopPropagation();
            }}
            asChild
          >
            <Link
              href={settingsHref}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SettingsIcon className="mr-2 h-4 w-4" />
              <Trans>Project Settings</Trans>
            </Link>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>

      {/* Duplicate Project Dialog */}
      <DuplicateProjectDialog
        workspaceId={workspaceId}
        projectId={projectId}
        projectType={projectType}
        projectName={projectName}
        open={duplicateDialogOpen}
        onOpenChange={setDuplicateDialogOpen}
      />
    </DropdownMenu>
  );
}
